/**
 * Regulatory Data Retention Policy Engine
 * ─────────────────────────────────────────────────────────────────────────────
 * Enforces mandatory retention periods for wastewater discharge monitoring
 * records under applicable legislation.  Regular users cannot delete records
 * within the retention window; only administrators can override (with audit
 * warning).  Records older than the retention period may be deleted normally.
 *
 * Standards covered:
 *   UK_EA    → 5 years  (EPR 2010 / Water Industry Act 1991)
 *   AU_EPA   → 7 years  (POEO Act 1997 NSW / EPA Act 2017 VIC / EPA Act 1994 QLD)
 *   EU_UWWTD → 5 years  (Directive 91/271/EEC / Water Framework Directive 2000/60/EC)
 */

export interface RetentionPolicy {
  standard: string;
  retentionYears: number;
  /** Full legal citation */
  regulationRef: string;
  /** Primary regulatory body */
  body: string;
  /** Types of data covered */
  description: string;
}

export const RETENTION_POLICIES: Record<string, RetentionPolicy> = {
  UK_EA: {
    standard: 'UK_EA',
    retentionYears: 5,
    regulationRef:
      'Environmental Permitting (England & Wales) Regulations 2010, Schedule 5, Para 1(1)(f); ' +
      'Water Industry Act 1991 s.121; EA Operational Instruction 1040_04',
    body: 'Environment Agency (EA) / Natural Resources Wales (NRW) / SEPA / NIEA',
    description:
      'Discharge monitoring records, trade effluent returns, permit compliance data, ' +
      'and related process engineering documentation.',
  },
  AU_EPA: {
    standard: 'AU_EPA',
    retentionYears: 7,
    regulationRef:
      'Protection of the Environment Operations Act 1997 (NSW) s.66; ' +
      'Environment Protection Act 2017 (VIC) s.49; ' +
      'Environmental Protection Act 1994 (QLD) s.320; ' +
      'Environment Protection Act 1993 (SA) s.33',
    body: 'State / Territory EPA',
    description:
      'Pollution monitoring data, Environment Protection Licence (EPL) records, ' +
      'waste discharge data, and related site assessment documentation.',
  },
  EU_UWWTD: {
    standard: 'EU_UWWTD',
    retentionYears: 5,
    regulationRef:
      'Council Directive 91/271/EEC Article 15; ' +
      'Water Framework Directive 2000/60/EC Article 14; ' +
      'Member State transposing legislation',
    body: 'European Environment Agency (EEA) / National Competent Authority',
    description:
      'Urban wastewater treatment discharge records, effluent monitoring data, ' +
      'permit compliance documentation, and treatment process engineering records.',
  },
};

/** Falls back to UK_EA (strictest common case) when standard is unknown */
export function getRetentionPolicy(standard: string | null | undefined): RetentionPolicy {
  if (standard) {
    const direct = RETENTION_POLICIES[standard];
    if (direct) return direct;
    // Normalise variants
    const norm = standard.replace(/[^A-Z_]/gi, '').toUpperCase();
    const match = RETENTION_POLICIES[norm];
    if (match) return match;
  }
  return RETENTION_POLICIES['UK_EA'];
}

/**
 * Returns true when `createdAt` is WITHIN the mandatory retention window —
 * i.e. the record must NOT be deleted by a non-admin user.
 */
export function isWithinRetention(createdAt: string | undefined, policy: RetentionPolicy): boolean {
  if (!createdAt) return true; // unknown date → assume protected
  try {
    const created = new Date(createdAt).getTime();
    if (isNaN(created)) return true;
    const retentionMs = policy.retentionYears * 365.25 * 24 * 60 * 60 * 1000;
    return Date.now() - created < retentionMs;
  } catch {
    return true;
  }
}

/**
 * Days remaining until the record exits the retention window.
 * Negative values mean it has already expired.
 */
export function retentionDaysRemaining(createdAt: string | undefined, policy: RetentionPolicy): number {
  if (!createdAt) return policy.retentionYears * 365;
  try {
    const created = new Date(createdAt).getTime();
    if (isNaN(created)) return policy.retentionYears * 365;
    const expiryMs = created + policy.retentionYears * 365.25 * 24 * 60 * 60 * 1000;
    return Math.ceil((expiryMs - Date.now()) / (24 * 60 * 60 * 1000));
  } catch {
    return policy.retentionYears * 365;
  }
}

/** Human-readable time remaining string */
export function retentionTimeRemaining(createdAt: string | undefined, policy: RetentionPolicy): string {
  const days = retentionDaysRemaining(createdAt, policy);
  if (days <= 0) return 'Retention period expired';
  if (days < 30) return `${days} day${days !== 1 ? 's' : ''}`;
  if (days < 365) return `${Math.round(days / 30)} month${Math.round(days / 30) !== 1 ? 's' : ''}`;
  const years = Math.ceil(days / 365);
  return `${years} year${years !== 1 ? 's' : ''}`;
}

/**
 * Detailed block message shown to regular users when deletion is prevented.
 */
export function retentionBlockMessage(policy: RetentionPolicy, createdAt: string | undefined, recordType = 'record'): string {
  const remaining = retentionTimeRemaining(createdAt, policy);
  return (
    `⚠ Deletion blocked — regulatory data retention\n\n` +
    `This ${recordType} is within the mandatory retention period and cannot be deleted.\n\n` +
    `Standard: ${policy.standard.replace('_', ' ')}\n` +
    `Retention period: ${policy.retentionYears} years\n` +
    `Regulatory body: ${policy.body}\n` +
    `Legal reference: ${policy.regulationRef}\n` +
    `Time remaining: ${remaining}\n\n` +
    `Contact your administrator if deletion is genuinely required.`
  );
}

/**
 * Short toast-friendly message for blocked deletions.
 */
export function retentionToastMessage(policy: RetentionPolicy, createdAt: string | undefined): string {
  const remaining = retentionTimeRemaining(createdAt, policy);
  return `Deletion blocked — record must be retained for ${remaining} under ${policy.standard.replace('_', ' ')} regulations (${policy.retentionYears}-year requirement). Admin override required.`;
}

/**
 * Admin override confirmation prompt text.
 */
export function adminOverridePrompt(policy: RetentionPolicy, createdAt: string | undefined, recordType = 'record'): string {
  const remaining = retentionTimeRemaining(createdAt, policy);
  return (
    `⚠ ADMIN OVERRIDE — Regulatory Compliance Warning\n\n` +
    `This ${recordType} is within the mandatory retention period:\n` +
    `  • Standard: ${policy.standard.replace('_', ' ')}\n` +
    `  • Retention: ${policy.retentionYears} years (${remaining} remaining)\n` +
    `  • Legal ref: ${policy.regulationRef}\n\n` +
    `Deleting this record may constitute a breach of environmental legislation.\n` +
    `This action will be logged.\n\n` +
    `Are you certain you want to permanently delete this ${recordType}?`
  );
}
